import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Connection, Keypair, LAMPORTS_PER_SOL, PublicKey, TransactionMessage, VersionedTransaction } from '@solana/web3.js';

import { useSolana } from '../hooks/useSolana';
import { useClipboard } from '../hooks/useClipboard';

import '../Home.css';
import { useSplToken } from '../hooks/useSplToken';
import { useRaydium } from '../hooks/useRaydium';
import { WSOL } from '@raydium-io/raydium-sdk';
import { useJito } from '../hooks/useJito';
import { jitoStressTestJitoTip } from '../../ConfigFees';
import { use0Slot } from '../hooks/use0Slot';

export function Home({
    connections,
    connection,
    csvFile,
    allWallets,
    allWalletRoles,
    bundleBuyValues,
    deployedToken,
    logMessage,
}: {
    connections: Connection[];
    connection: Connection;
    csvFile: File | null;
    allWallets: Keypair[];
    allWalletRoles: string[];
    bundleBuyValues: number[];
    deployedToken: string;
    logMessage: (message: string, type?: 'success' | 'error') => void;
}) {
    const { getMultipleSolBalances } = useSolana();
    const { getWalletsTokenStringBalances, getTokenDecimals } = useSplToken();
    const { fetchMarketAccountsByMints } = useRaydium();
    const { sendBundle } = useJito();
    const { copyToClipboard } = useClipboard();
    const { get0SlotFeeInstruction, send0SlotTransaction } = use0Slot();
    const [csvRows, setCsvRows] = useState<JSX.Element[]>([]);
    const navigate = useNavigate();

    const [refreshCounter, setRefreshCounter] = useState<number>(0);

    let bundlebuyCounter = 1;
    let mixCounter = 1;

    useEffect(() => {
        const fetchData = async () => {
            const rows = await renderCsvTable(allWallets, allWalletRoles, bundleBuyValues);
            if (rows) {
                setCsvRows(rows);
            }
        };

        fetchData();
        const intervalId = setInterval(fetchData, 10000);
        return () => {
            clearInterval(intervalId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshCounter, allWallets, allWalletRoles, bundleBuyValues, deployedToken]);

    const redirectToTokenCreate = () => {
        navigate('/CreateTokenForm');
    };

    const redirectToTokenManagerCsv = () => {
        navigate('/TokenManagerCsv');
    };

    function getRow(walletRole: string, wallet: Keypair, solBalance: string, mintedTokenBalance: string, bundleBuy: string) {
        const walletAddress = wallet.publicKey.toString();

        const role = walletRole.charAt(0).toUpperCase() + walletRole.slice(1).toLowerCase();
        let roleWithCounter = role;
        let bundleBuyStatus = '';

        if (role === 'Bundlebuy') {
            roleWithCounter = `${role}${bundlebuyCounter++}`;
        } else if (role === 'Mix') {
            roleWithCounter = `${role}${mixCounter++}`;
        }

        if (bundleBuy !== '' && bundleBuy !== '-') {
            bundleBuyStatus = solBalance >= bundleBuy ? '✅' : solBalance <= bundleBuy ? '❌' : '';
        }

        return (
            <tr key={walletAddress + walletRole}>
                <td>{roleWithCounter}</td>
                <td className='wallet-address-container'>
                    <span className='wallet-addresses-list'>{walletAddress.substring(0, 5) + '....' + walletAddress.substring(walletAddress.length - 6)}</span>
                    <button className='copy-button copy-wallets-button' onClick={() => copyToClipboard(walletAddress)}>
                        <span className='copy-button-active-effect'></span>
                    </button>
                </td>
                <td>{solBalance}</td>
                <td>{mintedTokenBalance}</td>
                <td>
                    <span className='bundle-buy-status'>{bundleBuyStatus}</span>
                    <span>{bundleBuy}</span>
                </td>
            </tr>
        );
    }

    async function renderCsvTable(allWallets: Keypair[], allWalletRoles: string[], bundleBuyValues: number[]) {
        if (allWallets.length === 0) return;
        const rows = [];

        const allSolBalances = await getMultipleSolBalances(
            connection,
            allWallets.map((keypair) => keypair.publicKey),
        );

        let tokenDecimals = 0;
        if (deployedToken !== '') {
            tokenDecimals = await getTokenDecimals(connection, new PublicKey(deployedToken));
        }

        const getWalletsTokenBalances =
            deployedToken === ''
                ? Array(allWallets.length).fill('-')
                : (
                      await getWalletsTokenStringBalances(
                          connection,
                          allWallets.map((wallet) => wallet.publicKey),
                          new PublicKey(deployedToken),
                      )
                  ).map((balance) => (Number(balance) / 10 ** tokenDecimals).toFixed(2));

        bundlebuyCounter = 1;
        mixCounter = 1;

        const bundleBuyStrings: string[] = [];
        let usedIndex = 0;
        for (let i = 0; i < allWalletRoles.length; i++) {
            const value = allWalletRoles[i] === 'BUNDLEBUY' ? bundleBuyValues[usedIndex++].toString() : '-';
            bundleBuyStrings.push(value);
        }

        for (let i = 0; i < allWallets.length; i++) {
            rows.push(
                getRow(allWalletRoles[i], allWallets[i], (allSolBalances[i] / LAMPORTS_PER_SOL).toFixed(2), getWalletsTokenBalances[i], bundleBuyStrings[i]),
            );
        }

        return rows;
    }

    async function testGetProgramAccounts(connections: Connection[]) {
        connections.forEach(async (connection) => {
            try {
                const startTime = Date.now();
                const accounts = await fetchMarketAccountsByMints(
                    connection,
                    new PublicKey('55BYndpUiceSiVVRQDiKHJ1EGTiYGZXwLuzzGYWmL3my'),
                    new PublicKey(WSOL.mint),
                );
                console.log(connection.rpcEndpoint);
                console.log(accounts);
                const finishTime = Date.now();
                logMessage(
                    `connection ${connection.rpcEndpoint} fetched market accounts. Responce time ${(finishTime - startTime) / 1000} second(s).`,
                    'success',
                );
            } catch (e) {
                logMessage(`connection ${connection.rpcEndpoint} failed to fetch market accounts: ${e}`, 'error');
            }
        });
    }

    async function jitoStressTest() {
        try {
            let promises = [];
            const bundlesNumber = 40;

            for (let i = 0; i < bundlesNumber; i++) {
                const promise = sendBundle(connection, [], allWallets[i], jitoStressTestJitoTip, false, logMessage);
                promises.push(promise);
            }
            logMessage('All bundles started processing');
            await Promise.all(promises).then(() => {
                logMessage('All bundles confirmed');
            });
        } catch (e) {
            logMessage(`Failed to send bundles: ${e}`, 'error');
        }
    }

    async function test0Slot() {
        try {
            const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();

            const feeInstruction = get0SlotFeeInstruction(allWallets[0].publicKey, 100000);
            const message = new TransactionMessage({
                payerKey: allWallets[0].publicKey,
                recentBlockhash: blockhash,
                instructions: [feeInstruction],
            }).compileToV0Message();
            const versionedTransaction = new VersionedTransaction(message);
            versionedTransaction.sign([allWallets[0]]);
            const tx = await send0SlotTransaction(versionedTransaction);
            logMessage(`0Slot transaction sent: ${tx}`);

            await connection.confirmTransaction({ blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight, signature: tx }, 'processed');
            logMessage('0Slot transaction confirmed', 'success');
        } catch (e) {
            logMessage(`Failed to send 0Slot transaction: ${e}`, 'error');
        }
    }

    return (
        <section>
            <div className='csv-operations'>
                <div>
                    <b>Loaded CSV:</b>
                    {csvFile ? <b className='loaded-csv-success'>Success</b> : <b className='loaded-csv-failed'>Failed</b>}
                </div>
                <div>
                    <button className='button-accent' onClick={() => setRefreshCounter(refreshCounter + 1)}>
                        Refresh Wallets
                    </button>
                    <button onClick={redirectToTokenCreate}>Create Token</button>
                    <button onClick={redirectToTokenManagerCsv}>Transfer SOL/Tokens</button>
                </div>
            </div>
            <div>
                <button onClick={() => testGetProgramAccounts(connections)}>Test GetProgramAccounts</button>
                <button onClick={jitoStressTest}>Jito Stress Test</button>
                <button onClick={test0Slot}>Test 0Slot</button>
            </div>
            <div className='home-table-wrapper'>
                <table className='parsed-csv-table'>
                    <thead>
                        <tr>
                            <th>Wallet Role</th>
                            <th>Address</th>
                            <th>SOL Balance</th>
                            <th>Minted Token Balance</th>
                            <th>BundleBuy</th>
                        </tr>
                    </thead>
                    <tbody>{csvRows}</tbody>
                </table>
            </div>
        </section>
    );
}
function receivePoolKeys() {
    throw new Error('Function not implemented.');
}
